import { computed, watch } from "vue";
import { useRouter } from "vue-router";

import { useBreadcrumbs } from "@/components/TBreadcrumbs/useBreadcrumbs";
import { useFlag } from "@/composables/useFlags/useFlag";
import {
  TITAN_REMOTE_CRUMBS_CHANGE_EVENT,
  isEmbedded,
} from "@/remote/contract";
import { isShellOwnedRoute } from "@/router/shellOwned";

/** One crumb as the host consumes it. Matches what red-app publishes. */
type PublishedCrumb = {
  id: string;
  label: string;
  href?: string;
};

/**
 * Publishes the crumbs BELOW the company to the host as DATA. The host draws the company trail
 * from its own data and appends these. Standalone this is inert: `TUpperNav` renders
 * `TBreadcrumbs` itself, and the layouts already hide that whole header when embedded.
 */
export function useCrumbsPublisher(host: HTMLElement | null): void {
  // Also stops `useBreadcrumbs` running twice standalone, where TBreadcrumbs owns it.
  if (!isEmbedded() || !host) return;

  const router = useRouter();
  const { isFeatureAvailable: isDashboardAvailableCustomer } = useFlag(
    "DASHBOARD_OVERVIEW_CUSTOMER",
  );

  const { pageItems } = useBreadcrumbs(isDashboardAvailableCustomer.value);

  // `resolve().href` applies the router base the host passed to `setRouterBase`, so the href is
  // host-absolute (`/thq/customer/…`) and its router can navigate it unchanged. A shell-owned
  // route gets no href: embedded, the guard bounces it to `/` and the user lands on sign-in's
  // default company instead of where the crumb said.
  const hrefOf = (url: string): { href: string } | Record<never, never> => {
    if (!url) return {};
    const resolved = router.resolve(url);
    return isShellOwnedRoute(resolved) ? {} : { href: resolved.href };
  };

  const crumbs = computed<PublishedCrumb[]>(() =>
    pageItems.value.map((crumb, index) => ({
      // Titan's crumbs carry no id, so one is derived. Index-prefixed because a trail can
      // legitimately repeat a name, and the host uses this as a track-by key.
      id: `${index}-${crumb.url || crumb.name}`,
      label: crumb.name,
      ...hrefOf(crumb.url),
    })),
  );

  watch(
    crumbs,
    (detail) => {
      // Bubbles + composed so the host can listen on `document` rather than on this element,
      // which it mounts lazily. Fires with an empty trail too — that is how a header clears.
      host.dispatchEvent(
        new CustomEvent<PublishedCrumb[]>(TITAN_REMOTE_CRUMBS_CHANGE_EVENT, {
          detail,
          bubbles: true,
          composed: true,
        }),
      );
    },
    { immediate: true },
  );
}

export default useCrumbsPublisher;
